import React, { useState, useRef } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
    Upload, FileSpreadsheet, CheckCircle, AlertTriangle, X, Layers, TrendingUp, Trash2,
} from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "number") {
        if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
        if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
        return v.toLocaleString("en-IN");
    }
    return String(v);
};

export default function WorkbookIngest() {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [wipeExisting, setWipeExisting] = useState(true);
    const [selectedRows, setSelectedRows] = useState({});
    const fileRef = useRef(null);
    const navigate = useNavigate();

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/workbook/ingest/preview", fd);
            setPreview(r.data);
            const sel = {};
            (r.data.rows || []).forEach((row, i) => { sel[i] = true; });
            setSelectedRows(sel);
            toast.success(`Parsed ${r.data.row_count} rows · ${r.data.unique_clients} clients · ${r.data.issue_count} issues`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const commit = async () => {
        if (!preview) return;
        const rowsToCommit = preview.rows.filter((_, i) => selectedRows[i]);
        if (rowsToCommit.length === 0) {
            toast.error("Select at least one row to commit");
            return;
        }
        const msg = wipeExisting
            ? `Wipe all ${preview.rows.length} demo clients/projects/contracts and import ${rowsToCommit.length} rows?`
            : `Add ${rowsToCommit.length} rows alongside existing clients/projects/contracts?`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            const r = await api.post("/workbook/ingest/commit", { wipe_existing: wipeExisting, rows: rowsToCommit });
            toast.success(`Imported: ${r.data.clients_created} clients · ${r.data.projects_created} projects · ${r.data.contracts_created} contracts`);
            setTimeout(() => navigate("/clients"), 1200);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const toggleRow = (i) => setSelectedRows({ ...selectedRows, [i]: !selectedRows[i] });
    const toggleAll = (v) => {
        const sel = {};
        (preview.rows || []).forEach((_, i) => { sel[i] = v; });
        setSelectedRows(sel);
    };

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PHASE A · WORKBOOK INGEST</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <FileSpreadsheet className="text-[var(--brand-amber)]" size={26} />
                    Client Workbook Ingest
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Upload "On going projects working.xlsx" → parse → detect issues → commit to Client Master
                </p>
            </div>

            {!preview && (
                <Panel testId="upload-panel" title="Step 1 · Upload workbook">
                    <div
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
                        className="border-2 border-dashed border-panel hover:border-[var(--brand-amber)] p-12 text-center cursor-pointer transition-colors"
                    >
                        <Upload className="mx-auto text-[var(--brand-amber)] mb-3" size={40} />
                        <div className="text-[var(--text-primary)] font-mono-fin text-sm">Drop your .xlsx workbook here</div>
                        <div className="text-[10px] text-[var(--text-secondary)] mt-2 font-mono-fin uppercase tracking-wider">Or click to browse · openpyxl parser · reads "All Client" sheet</div>
                        <input
                            data-testid="workbook-file-input"
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,.xlsm"
                            className="hidden"
                            onChange={(e) => upload(e.target.files?.[0])}
                        />
                    </div>
                </Panel>
            )}

            {busy && (
                <Panel testId="busy">
                    <div className="text-center py-8 text-[var(--brand-amber)] font-mono-fin uppercase tracking-widest text-sm">
                        Processing…
                    </div>
                </Panel>
            )}

            {preview && (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Panel testId="kpi-rows">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Rows</div>
                            <div className="text-2xl font-display font-black text-[var(--text-primary)] mt-1">{preview.row_count}</div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">{preview.unique_clients} unique clients</div>
                        </Panel>
                        <Panel testId="kpi-contract">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Contract Value</div>
                            <div className="text-2xl font-display font-black text-[var(--text-primary)] mt-1">{fmt(preview.summary.total_contract_value)}</div>
                        </Panel>
                        <Panel testId="kpi-received">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Received (Valid)</div>
                            <div className="text-2xl font-display font-black text-[var(--pos)] mt-1">{fmt(preview.summary.total_received_valid)}</div>
                        </Panel>
                        <Panel testId="kpi-outstanding">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Est. Outstanding</div>
                            <div className="text-2xl font-display font-black text-[var(--brand-amber)] mt-1">{fmt(preview.summary.total_outstanding_est)}</div>
                        </Panel>
                        <Panel testId="kpi-issues">
                            <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)]">Data Issues</div>
                            <div className={`text-2xl font-display font-black mt-1 ${preview.issue_count === 0 ? "text-[var(--pos)]" : "text-[var(--neg)]"}`}>{preview.issue_count}</div>
                            <div className="text-[10px] font-mono-fin text-[var(--text-muted)]">{preview.summary.bloated_sheets.length} bloated sheet(s)</div>
                        </Panel>
                    </div>

                    {/* Sheet metadata */}
                    <Panel testId="sheets-meta" title={`Step 2 · Workbook structure · ${preview.sheets_total} sheets`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs font-mono-fin">
                            {preview.sheets.map((s) => (
                                <div key={s.name} className={`border border-panel px-2 py-1.5 flex items-center justify-between ${s.bloat ? "bg-[var(--neg)]/10 border-[var(--neg)]" : ""}`}>
                                    <span className="text-[var(--text-primary)]/90 truncate">{s.name}</span>
                                    <span className="text-[var(--text-muted)] ml-2 whitespace-nowrap">
                                        {s.rows}×{s.cols}
                                        {s.bloat && <span className="text-[var(--neg)] ml-1">⚠ bloat</span>}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Panel>

                    {/* Issue list */}
                    {preview.issues.length > 0 && (
                        <Panel testId="issues-panel" title={`Step 3 · Data-quality issues · ${preview.issues.length}`} subtitle="Review before committing">
                            <div className="max-h-[240px] overflow-y-auto space-y-1">
                                {preview.issues.map((iss, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs border-b border-panel/60 py-1.5">
                                        <AlertTriangle size={12} className="text-[var(--brand-amber)] mt-0.5 flex-shrink-0" />
                                        <div className="flex-1">
                                            <span className="text-[var(--text-primary)]/70 font-mono-fin text-[10px]">Row {iss.row}</span>
                                            <span className="text-[var(--text-primary)]/90 ml-2">{iss.client}</span>
                                            <span className="text-[var(--brand-amber)] ml-2">· {iss.issue}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Panel>
                    )}

                    {/* Row table */}
                    <Panel
                        testId="rows-table"
                        title={`Step 4 · Rows to import · ${Object.values(selectedRows).filter(Boolean).length}/${preview.rows.length} selected`}
                        subtitle="Uncheck rows you don't want to commit"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <button
                                data-testid="select-all-btn"
                                onClick={() => toggleAll(true)}
                                className="border border-panel px-2 py-1 text-[10px] font-mono-fin uppercase hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)]"
                            >
                                Select All
                            </button>
                            <button
                                data-testid="clear-all-btn"
                                onClick={() => toggleAll(false)}
                                className="border border-panel px-2 py-1 text-[10px] font-mono-fin uppercase hover:border-[var(--brand-amber)] hover:text-[var(--brand-amber)]"
                            >
                                Clear All
                            </button>
                            <div className="ml-auto flex items-center gap-2">
                                <label className="flex items-center gap-1 text-xs text-[var(--text-primary)]/80 cursor-pointer">
                                    <input
                                        data-testid="wipe-existing-toggle"
                                        type="checkbox"
                                        checked={wipeExisting}
                                        onChange={(e) => setWipeExisting(e.target.checked)}
                                        className="accent-[var(--brand-amber)]"
                                    />
                                    <span className="font-mono-fin uppercase text-[10px]">Wipe demo data first</span>
                                </label>
                                <button
                                    data-testid="commit-btn"
                                    onClick={commit}
                                    disabled={busy}
                                    className="bg-[var(--brand-amber)] text-black px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    <CheckCircle size={12} /> Commit & Redirect
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto max-h-[480px]">
                            <table className="w-full text-xs font-body">
                                <thead className="sticky top-0 bg-panel">
                                    <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2 px-2">Pick</th>
                                        <th className="text-left py-2 px-2">Client Name</th>
                                        <th className="text-left py-2 px-2">Location</th>
                                        <th className="text-right py-2 px-2">Contract</th>
                                        <th className="text-right py-2 px-2">Work Done</th>
                                        <th className="text-right py-2 px-2">Received</th>
                                        <th className="text-right py-2 px-2">Due</th>
                                        <th className="text-left py-2 px-2">Stage</th>
                                        <th className="text-left py-2 px-2">Issues</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.rows.map((r, i) => (
                                        <tr key={i} data-testid={`preview-row-${i}`} className={`border-b border-panel/60 hover:bg-panel/40 ${r.issues.length > 0 ? "bg-[var(--brand-amber)]/5" : ""}`}>
                                            <td className="py-1.5 px-2">
                                                <input
                                                    data-testid={`row-check-${i}`}
                                                    type="checkbox"
                                                    checked={!!selectedRows[i]}
                                                    onChange={() => toggleRow(i)}
                                                    className="accent-[var(--brand-amber)]"
                                                />
                                            </td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]">{r.client_name}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/70">{r.location}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--brand-amber)]">{fmt(r.contract_value)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(r.work_done)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin">{fmt(r.amount_received)}</td>
                                            <td className="text-right py-1.5 px-2 font-mono-fin text-[var(--text-primary)]/60">{fmt(r.total_due)}</td>
                                            <td className="py-1.5 px-2 text-[var(--text-primary)]/70 text-[11px]">{r.current_stage || "—"}</td>
                                            <td className="py-1.5 px-2 text-[10px]">
                                                {r.issues.length > 0 && (
                                                    <span className="text-[var(--brand-amber)]" title={r.issues.join("\n")}>
                                                        ⚠ {r.issues.length}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
}
